import React, { FunctionComponent } from 'react';
import { Box, Text } from '@rsos/base-components';
import {
  WrapperContainer,
  ULTagContainer,
  LiTagContainer,
  InputContainer,
  ClearIconComponent,
  ErrorComponent,
  StyledMultiEntryTextArea,
  InputTextLabel,
} from './MultiEntryInput.styles';
import { getValidatedNewTagEnhanced } from './helpers';

export interface MultiInputTag {
  id: string;
  value: string;
  isValid: boolean;
  type?: string;
  error?: string;
}

export interface MultiEntryInputProps {
  value: string;
  label?: string | null;
  name: string;
  placeholder?: string;
  hiddenValue?: MultiInputTag[];
  hiddenInputName: string;
  tags: MultiInputTag[];
  updateTags: (tags: MultiInputTag[]) => void;
  validator: (value: string) => boolean;
  setFieldValue: (field: string, value: string | MultiInputTag[]) => void;
  handleChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  error?: string;
  inputType?: 'tel' | 'email' | 'mixed';
  prefixComponent?: React.ReactNode | null;
  disabled?: boolean;
  className?: string;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  multiWordValues?: boolean;
  icons?: { [key: string]: React.ComponentType };
}

const spaceKeyCode = 32;

const MultiEntryInput: FunctionComponent<MultiEntryInputProps> = ({
  value,
  label,
  name,
  placeholder,
  hiddenValue,
  hiddenInputName,
  tags,
  updateTags,
  validator,
  setFieldValue,
  handleChange,
  error,
  inputType,
  prefixComponent,
  disabled,
  className,
  onBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e;
  },
  multiWordValues = false,
  icons = {},
}) => {
  const handleRemoveTag = (tag: MultiInputTag) => {
    const newTags = tags.filter(t => t.id !== tag.id);
    updateTags(newTags);
    setFieldValue(hiddenInputName, newTags);
  };

  multiWordValues = inputType === 'tel' || multiWordValues;

  /* When the input is a phone number, to format on the fly to be like (347) 771-7556
   ** when adding and removing digits, we apply util functions.
   ** Apply different formatting functions based on whether the number starts with 1 or not
   ** Note: Live formatting is disabled for mixed mode to avoid ambiguity */
  const handleChangeMultiEntryPhone = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    setFieldValue: (field: string, value: string | MultiInputTag[]) => void,
  ) => {
    // Skip live formatting for mixed mode
    if (inputType === 'mixed') {
      return;
    }

    const { value } = e.target;
    setFieldValue(name, value);
  };

  /* Invoked when a copy paste event is detected */
  const handlePaste = (
    e: React.ClipboardEvent<HTMLTextAreaElement>,
    setFieldValue: (field: string, value: string | MultiInputTag[]) => void,
  ) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const result = text
      .split(/[\n,]+/)
      .reduce((acc: string[], curr: string) => {
        const trimmedValue = curr?.trim();
        if (trimmedValue) {
          acc.push(trimmedValue);
        }
        return acc;
      }, []);
    const validatedTagsArray: MultiInputTag[] = result.map(v =>
      getValidatedNewTagEnhanced(v, inputType, validator),
    );
    updateTags([...tags, ...validatedTagsArray]);
    setFieldValue(name, '');
    setFieldValue(hiddenInputName, [...tags, ...validatedTagsArray]);
  };

  /* When Enter key or a space key is pressed we validate the input  */
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    setFieldValue: (field: string, value: string | MultiInputTag[]) => void,
  ) => {
    let { value } = e.currentTarget;
    if ((e.key === 'Enter' || e.keyCode === spaceKeyCode) && value) {
      // For multi-word tags, a comma-space breaks the word, but just a space does not.
      if (e.keyCode === spaceKeyCode && multiWordValues) {
        if (value[value.length - 1] === ',') {
          value = value.slice(0, -1);
        } else {
          return;
        }
      }
      e.preventDefault();
      const validatedNewTag: MultiInputTag = getValidatedNewTagEnhanced(
        value,
        inputType,
        validator,
      );
      updateTags([...tags, validatedNewTag]);
      setFieldValue(name, '');
      setFieldValue(hiddenInputName, [...tags, validatedNewTag]);
    }
  };

  /* When leaving the input we validate the entered value  */
  const handleBlur = (
    e: React.FocusEvent<HTMLTextAreaElement>,
    setFieldValue: (field: string, value: string | MultiInputTag[]) => void,
  ) => {
    const { value } = e.target;
    if (value) {
      e.preventDefault();
      const validatedNewTag: MultiInputTag = getValidatedNewTagEnhanced(
        value,
        inputType,
        validator,
      );
      updateTags([...tags, validatedNewTag]);
      setFieldValue(name, '');
      setFieldValue(hiddenInputName, [...tags, validatedNewTag]);
    }
    onBlur && onBlur(e);
  };
  return (
    <Box className={className}>
      {label && <InputTextLabel>{label}</InputTextLabel>}
      <WrapperContainer error={error}>
        <input
          type="hidden"
          name={hiddenInputName}
          value={hiddenValue ? JSON.stringify(hiddenValue) : ''}
        />
        {prefixComponent && prefixComponent}
        <ULTagContainer>
          {tags.map((tag: MultiInputTag) => {
            const IconComponent =
              tag.type && icons[tag.type] ? icons[tag.type] : null;
            const hasIcon = !!IconComponent;

            return (
              <LiTagContainer
                key={tag.id}
                isValid={tag.isValid}
                hasIcon={hasIcon}
              >
                {IconComponent && React.createElement(IconComponent)}
                <Text size="small">{tag.value}</Text>
                <ClearIconComponent
                  onClick={() => {
                    handleRemoveTag(tag);
                  }}
                />
              </LiTagContainer>
            );
          })}
          <InputContainer hasPrefixComponent={!!prefixComponent}>
            <StyledMultiEntryTextArea
              rows={1}
              name={name}
              onChange={
                inputType === 'tel'
                  ? (e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      handleChangeMultiEntryPhone(e, setFieldValue)
                  : handleChange
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) =>
                handleKeyDown(e, setFieldValue)
              }
              value={value}
              placeholder={placeholder}
              onPaste={(e: React.ClipboardEvent<HTMLTextAreaElement>) =>
                handlePaste(e, setFieldValue)
              }
              disabled={disabled}
              onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
                handleBlur(e, setFieldValue)
              }
            />
          </InputContainer>
        </ULTagContainer>
      </WrapperContainer>
      {error && <ErrorComponent error={error} />}
    </Box>
  );
};

export default MultiEntryInput;
